import { request } from '@/api/request';
import type { Discussion as DiscussionModel } from '@/types/models/discussion';

export type CommentView = {
  id: string;
  viewAt: string;
};

export type CommentAttachment = {
  commentID: string;
  createdAt: string;
  createdBy: string;
  uploadID: string;
};

export type Comment = {
  id: string;
  discussionID: string;
  message: string;
  createdAt: string;
  createdBy: string;
  orgID: string;
  attachments: CommentAttachment[] | null;
};

export type UserData = {
  firstName: string;
  lastName: string;
  orgID: string;
};

export type OrgData = {
  name: string;
  logo: string;
};

export type Discussion = {
  id: string;
  associatedID: string;
  associatedType: string;
  clientID: string;
  supplierID: string;
  createdAt: string;
  createdBy: string;
  archivedAt: string | null;
  comments: Comment[];
  latestCommentViewed: CommentView | null;
};

export type InboxDiscussion = {
  id: string;
  associatedID: string;
  associatedType: string;
  clientID: string;
  supplierID: string;
  createdAt: string;
  createdBy: string;
  archivedAt: string | null;
  lastMessage: Comment;
  latestCommentViewed: CommentView | null;
};

export type DiscussionPreviewsVariables = {
  role: string;
  participationType: string;
  archived: boolean;
  orgFilter: string[];
  order?: string;
  page?: number;
  limit?: number;
};

export type DiscussionPreviewResponse = {
  discussions: InboxDiscussion[] | null;
  users: Record<string, UserData>;
  orgs: Record<string, OrgData>;
};

// TODO: we will hopefully refactor endpoints and
// get rid of the /inbox postfix in the urls in the future
const getDiscussionPreviews = (payload: DiscussionPreviewsVariables) =>
  request<DiscussionPreviewResponse>('POST', '/discussions/inbox', payload);

const getDiscussion = (id: string) =>
  request<{ discussion: Discussion; users: UserData; orgs: OrgData }>(
    'GET',
    `/discussions/${id}/inbox`,
  );

const startDiscussion = (
  associatedID: string,
  associatedType: any,
  message: any,
  clientID: string,
  supplierID: string,
  attachments: any,
) =>
  request('POST', `/discussion`, {
    associatedID,
    associatedType,
    message,
    clientID,
    supplierID,
    attachments,
  });

const replyDiscussion = (discussionID: string, message: any, attachments: any) =>
  request('POST', `/discussion/${discussionID}`, {
    message,
    attachments,
  });

export type AssociatedDiscussionsResponse = {
  discussions: DiscussionModel[];
  users: Record<string, UserData>;
  orgs: Record<string, OrgData>;
};

const getDiscussionsByAssociatedID = (associatedID: string) =>
  request<AssociatedDiscussionsResponse>('GET', `/discussions/associated/${associatedID}`);

const archiveDiscussion = (discussionID: string) =>
  request('POST', `/discussion/${discussionID}/archive`);

const unarchiveDiscussion = (discussionID: string) =>
  request('POST', `/discussion/${discussionID}/unarchive`);

const markCommentsAsSeen = (discussionId: string, commentIds: string[]) =>
  request('POST', `/discussions/${discussionId}/markCommentsAsSeen`, { commentIds });

export default {
  getDiscussionPreviews,
  getDiscussion,
  getDiscussionsByAssociatedID,
  startDiscussion,
  replyDiscussion,
  archiveDiscussion,
  unarchiveDiscussion,
  markCommentsAsSeen,
};
